import appInsightsService, { ENV, LOG_LEVEL } from "./appInsights";

type LogLevel = LOG_LEVEL;
type LogSource = "Axios" | "ReactQuery" | "Auth" | "AppInsights" | "Other";
type ConsoleMethod = "error" | "warn" | "log" | "debug";

interface LogConfig {
  method: ConsoleMethod;
  priority: number;
}

class Logger {
  private static instance: Logger;
  private readonly env = import.meta.env.MODE as ENV;
  private readonly isDev = this.env === ENV.DEVELOPMENT;
  private readonly threshold = appInsightsService.logLevel;

  private logConfigs: Record<LogLevel, LogConfig> = {
    [LOG_LEVEL.DEBUG]: { method: "debug", priority: 0 },
    [LOG_LEVEL.INFO]: { method: "log", priority: 1 },
    [LOG_LEVEL.WARN]: { method: "warn", priority: 2 },
    [LOG_LEVEL.ERROR]: { method: "error", priority: 3 },
  };

  private constructor() {}

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private shouldLog(level: LogLevel): boolean {
    return (
      this.logConfigs[level].priority >=
      this.logConfigs[this.threshold].priority
    );
  }

  private log(
    level: LogLevel,
    source: LogSource,
    message: string,
    details?: unknown
  ) {
    if (!this.shouldLog(level)) return;

    const config = this.logConfigs[level];
    const timestamp = new Date().toISOString();

    // Dev: pretty console logs
    if (this.isDev) {
      console.group(`[${source}] ${level.toUpperCase()}`);
      console[config.method]("Message:", message);
      if (details !== undefined) console[config.method]("Details:", details);
      console[config.method]("Time:", timestamp);
      console.groupEnd();
    }

    // All environments: App Insights
    if (appInsightsService.isReady()) {
      if (level === LOG_LEVEL.ERROR) {
        appInsightsService.trackException(
          details instanceof Error ? details : new Error(message),
          { message, source, environment: this.env }
        );
      } else {
        appInsightsService.trackTrace(message, undefined, {
          level,
          source,
          environment: this.env,
        });
      }
    }
  }

  error(source: LogSource, message: string, details?: unknown) {
    this.log(LOG_LEVEL.ERROR, source, message, details);
  }
  warn(source: LogSource, message: string, details?: unknown) {
    this.log(LOG_LEVEL.WARN, source, message, details);
  }
  info(source: LogSource, message: string, details?: unknown) {
    this.log(LOG_LEVEL.INFO, source, message, details);
  }
  debug(source: LogSource, message: string, details?: unknown) {
    this.log(LOG_LEVEL.DEBUG, source, message, details);
  }
}

export const logger = Logger.getInstance();
